import React, { useEffect, useState, useRef } from "react";
import { ym } from "../lib/dates";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Plus, PencilSimple, Trash, Printer, FileArrowUp, Download, BookOpenText, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import api, { formatApiError, formatMonth } from "../lib/api";
import { useAuth } from "../lib/auth";
import { generatePDF } from "../lib/pdf";

const TABS = [
  { key: "list", label: "Daftar" },
  { key: "monthly", label: "Input Per Bulan" },
  { key: "rekap", label: "Rekap Per Lembaga" },
];

export default function RaporBiah() {
  const { user } = useAuth();
  const isKaryawan = user?.role === "karyawan";
  const canManage = ["superadmin", "admin", "hr"].includes(user?.role);
  const [tab, setTab] = useState("list");
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [lembagas, setLembagas] = useState([]);
  const [filterPeriod, setFilterPeriod] = useState(ym());
  const [filterLembaga, setFilterLembaga] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [monthlyForm, setMonthlyForm] = useState({ period: filterPeriod, lembaga_id: "all", rows: {} });
  const [rekap, setRekap] = useState(null);
  const fileInput = useRef();

  const load = async () => {
    const [r, e, l] = await Promise.all([
      isKaryawan ? api.get("/rapor-biah") : api.get(`/rapor-biah${filterPeriod !== "all" ? `?period=${filterPeriod}` : ""}`),
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/lembaga").catch(() => ({ data: [] })),
    ]);
    setItems(r.data); setEmployees(e.data); setLembagas(l.data);
  };
  useEffect(() => { load(); }, [filterPeriod, isKaryawan]);

  const empMap = Object.fromEntries(employees.map((e) => [e.employee_id, e]));
  const lembagaMap = Object.fromEntries(lembagas.map((l) => [l.id, l.name]));

  const openNew = () => {
    setEditing(null);
    setForm({ employee_id: employees[0]?.employee_id || "", period: filterPeriod === "all" ? ym() : filterPeriod, durasi_menit: 0, jumlah_halaman: 0, target_tercapai: false, keterangan: "" });
    setOpen(true);
  };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        employee_id: form.employee_id, period: form.period,
        durasi_menit: Number(form.durasi_menit || 0),
        jumlah_halaman: Number(form.jumlah_halaman || 0),
        rata_rata_halaman_per_hari: form.rata_rata_halaman_per_hari ? Number(form.rata_rata_halaman_per_hari) : null,
        target_tercapai: !!form.target_tercapai,
        keterangan: form.keterangan || null,
      };
      if (editing) await api.put(`/rapor-biah/${editing.id}`, payload);
      else await api.post("/rapor-biah", payload);
      toast.success("Rapor bi'ah tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Hapus rapor bi'ah?")) return; try { await api.delete(`/rapor-biah/${id}`); load(); toast.success("Terhapus"); } catch { toast.error("Gagal"); } };

  const downloadTemplate = () => {
    const headers = ["employee_id", "period", "durasi_menit", "jumlah_halaman", "rata_rata_halaman_per_hari", "target_tercapai", "keterangan"];
    const example = ["EMP-100001", "2026-02", 600, 30, 1, true, "Juz 15"];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    XLSX.utils.book_append_sheet(wb, ws, "Rapor Biah");
    XLSX.writeFile(wb, "template-rapor-biah.xlsx");
  };

  const importFile = async (file) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet);
      const rows = data.filter((d) => d.employee_id && d.period).map((d) => ({
        employee_id: String(d.employee_id), period: String(d.period),
        durasi_menit: Number(d.durasi_menit || 0), jumlah_halaman: Number(d.jumlah_halaman || 0),
        rata_rata_halaman_per_hari: d.rata_rata_halaman_per_hari ? Number(d.rata_rata_halaman_per_hari) : null,
        target_tercapai: [true, "true", "TRUE", "YA", "ya", 1, "1"].includes(d.target_tercapai),
        keterangan: d.keterangan ? String(d.keterangan) : null,
      }));
      const { data: res } = await api.post("/rapor-biah/bulk", rows);
      toast.success(`${res.inserted} rapor bi'ah diimport`);
      if (res.errors?.length) alert(`${res.errors.length} gagal:\n` + res.errors.slice(0, 10).map((e) => `${e.employee_id}: ${e.error}`).join("\n"));
      load();
    } catch (e) { toast.error("Gagal import: " + (e.message || "")); }
  };

  const printOne = (r) => {
    const emp = empMap[r.employee_id] || {};
    generatePDF({
      title: `Rapor Bi'ah Qur'an – ${formatMonth(r.period)}`,
      subtitle: emp.name || r.employee_id,
      headers: ["Aspek", "Nilai"],
      rows: [
        ["Durasi Total", `${r.durasi_menit} menit`],
        ["Jumlah Halaman", String(r.jumlah_halaman)],
        ["Rata-Rata Halaman/Hari", String(r.rata_rata_halaman_per_hari)],
        ["Target Tercapai", r.target_tercapai ? "YA" : "TIDAK"],
        ["Keterangan", r.keterangan || "-"],
      ],
      meta: { "Nama": emp.name || "-", "Jabatan": emp.position || "-", "Lembaga": lembagaMap[emp.lembaga_id] || "-", "Bulan": formatMonth(r.period) },
      filename: `rapor-biah-${r.employee_id}-${r.period}.pdf`,
    });
  };

  // Monthly bulk
  const loadMonthlyGrid = () => {
    const targetEmps = employees.filter((e) => monthlyForm.lembaga_id === "all" || e.lembaga_id === monthlyForm.lembaga_id);
    const rows = {};
    targetEmps.forEach((e) => {
      const existing = items.find((r) => r.employee_id === e.employee_id && r.period === monthlyForm.period);
      rows[e.employee_id] = existing ? {
        durasi_menit: existing.durasi_menit, jumlah_halaman: existing.jumlah_halaman,
        rata_rata_halaman_per_hari: existing.rata_rata_halaman_per_hari, target_tercapai: existing.target_tercapai, keterangan: existing.keterangan || "",
      } : { durasi_menit: 0, jumlah_halaman: 0, rata_rata_halaman_per_hari: null, target_tercapai: false, keterangan: "" };
    });
    setMonthlyForm({ ...monthlyForm, rows });
  };
  useEffect(() => { if (tab === "monthly" && employees.length) loadMonthlyGrid(); }, [tab, employees, monthlyForm.period, monthlyForm.lembaga_id]);

  const saveMonthly = async () => {
    const rows = Object.entries(monthlyForm.rows).map(([employee_id, r]) => ({
      employee_id, period: monthlyForm.period,
      durasi_menit: Number(r.durasi_menit || 0), jumlah_halaman: Number(r.jumlah_halaman || 0),
      rata_rata_halaman_per_hari: r.rata_rata_halaman_per_hari ? Number(r.rata_rata_halaman_per_hari) : null,
      target_tercapai: !!r.target_tercapai, keterangan: r.keterangan || null,
    }));
    if (!rows.length) return toast.error("Tidak ada baris");
    try { const { data: res } = await api.post("/rapor-biah/bulk", rows); toast.success(`${res.inserted} tersimpan`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadRekap = async () => {
    try {
      const q = new URLSearchParams({ period: filterPeriod === "all" ? ym() : filterPeriod });
      if (filterLembaga !== "all") q.set("lembaga_id", filterLembaga);
      const { data } = await api.get(`/rapor-biah/rekap?${q}`);
      setRekap(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { if (tab === "rekap") loadRekap(); }, [tab, filterPeriod, filterLembaga]);

  const printRekap = () => {
    if (!rekap) return;
    const lembagaLabel = filterLembaga === "all" ? "Seluruh Lembaga" : (lembagaMap[filterLembaga] || "-");
    generatePDF({
      title: `Rekap Rapor Bi'ah Qur'an ${formatMonth(rekap.period)}`, subtitle: lembagaLabel,
      headers: ["Guru", "Jabatan", "Lembaga", "Durasi (mnt)", "Halaman", "Avg/Hari", "Target", "Keterangan"],
      rows: rekap.rows.map((r) => [r.name || r.employee_id, r.position || "-", r.lembaga_name || "-", r.durasi_menit, r.jumlah_halaman, r.rata_rata_halaman_per_hari, r.target_tercapai ? "YA" : "TIDAK", r.keterangan || "-"]),
      meta: { "Periode": formatMonth(rekap.period), "Lembaga": lembagaLabel, "Total": rekap.count },
      filename: `rekap-rapor-biah-${rekap.period}-${filterLembaga}.pdf`,
    });
  };
  const exportRekapExcel = () => {
    if (!rekap) return;
    const headers = ["Guru", "Jabatan", "Lembaga", "Durasi (menit)", "Halaman", "Rata²/Hari", "Target Tercapai", "Keterangan"];
    const rows = rekap.rows.map((r) => [r.name || r.employee_id, r.position || "", r.lembaga_name || "", r.durasi_menit, r.jumlah_halaman, r.rata_rata_halaman_per_hari, r.target_tercapai ? "YA" : "TIDAK", r.keterangan || ""]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    XLSX.utils.book_append_sheet(wb, ws, "Rekap Biah");
    XLSX.writeFile(wb, `rekap-rapor-biah-${rekap.period}-${filterLembaga}.xlsx`);
  };

  return (
    <div data-testid="rapor-biah-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1 flex items-center gap-2"><BookOpenText size={14} weight="duotone" /> Human Resource</div>
          <h1 className="font-display text-3xl font-black text-foreground">Rapor Bi&apos;ah Qur&apos;an</h1>
          <p className="text-muted-foreground text-sm mt-1">Pantau tilawah/tahfidz guru & karyawan per bulan.</p>
        </div>
      </div>

      {!isKaryawan && (
        <div className="mb-4 flex gap-1 border-b border-border" data-testid="biah-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`biah-tab-${t.key}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "text-[#0B8D95] border-[#0B8D95]" : "text-muted-foreground border-transparent hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {(isKaryawan || tab === "list") && (
        <>
          <div className="bg-card border border-border rounded-xl p-3 mb-3 flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={filterPeriod === "all" ? "" : filterPeriod} onChange={(e) => setFilterPeriod(e.target.value || "all")} className="w-40" data-testid="biah-period-filter" /></div>
            {canManage && (
              <div className="flex gap-2 flex-wrap ml-auto">
                <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="biah-template-button"><Download size={14} className="mr-1.5" />Template Excel</Button>
                <input ref={fileInput} type="file" accept=".xlsx,.xls" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0])} data-testid="biah-import-file" />
                <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()} data-testid="biah-import-button"><FileArrowUp size={14} className="mr-1.5" />Import Excel</Button>
                <Button size="sm" onClick={openNew} data-testid="biah-add-button"><Plus size={14} className="mr-1.5" />Buat Rapor Bi&apos;ah</Button>
              </div>
            )}
          </div>
          <div className="bg-card border border-border rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  {!isKaryawan && <th className="px-4 py-3">Guru</th>}
                  <th className="px-4 py-3">Bulan</th>
                  <th className="px-4 py-3 text-right">Durasi (menit)</th>
                  <th className="px-4 py-3 text-right">Halaman</th>
                  <th className="px-4 py-3 text-right">Avg/Hari</th>
                  <th className="px-4 py-3 text-center">Target</th>
                  <th className="px-4 py-3">Keterangan</th>
                  <th className="px-4 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                    {!isKaryawan && <td className="px-4 py-2.5 font-semibold">{empMap[r.employee_id]?.name || r.employee_id}</td>}
                    <td className="px-4 py-2.5 font-mono text-xs">{formatMonth(r.period)}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.durasi_menit}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.jumlah_halaman}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{r.rata_rata_halaman_per_hari}</td>
                    <td className="px-4 py-2.5 text-center">
                      {r.target_tercapai
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold"><CheckCircle size={12} weight="fill" />YA</span>
                        : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-bold"><XCircle size={12} weight="fill" />TIDAK</span>}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground truncate max-w-xs">{r.keterangan || "-"}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => printOne(r)} className="p-1.5 hover:bg-muted rounded" data-testid={`biah-print-${r.id}`}><Printer size={14} /></button>
                      {canManage && <>
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-muted rounded" data-testid={`biah-edit-${r.id}`}><PencilSimple size={14} /></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" data-testid={`biah-delete-${r.id}`}><Trash size={14} /></button>
                      </>}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">Belum ada rapor bi&apos;ah</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isKaryawan && tab === "monthly" && canManage && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Bulan</Label><Input type="month" value={monthlyForm.period} onChange={(e) => setMonthlyForm({ ...monthlyForm, period: e.target.value })} className="w-40" data-testid="biah-monthly-period" /></div>
            <div><Label className="text-xs">Lembaga</Label>
              <Select value={monthlyForm.lembaga_id} onValueChange={(v) => setMonthlyForm({ ...monthlyForm, lembaga_id: v })}>
                <SelectTrigger className="w-56" data-testid="biah-monthly-lembaga"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Semua Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button className="ml-auto" onClick={saveMonthly} data-testid="biah-monthly-save"><Plus size={14} className="mr-1.5" />Simpan Semua</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted text-left uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">Guru</th>
                  <th className="px-2 py-2 font-semibold text-right">Durasi (mnt)</th>
                  <th className="px-2 py-2 font-semibold text-right">Halaman</th>
                  <th className="px-2 py-2 font-semibold text-right">Avg/Hari</th>
                  <th className="px-2 py-2 font-semibold text-center">Target</th>
                  <th className="px-2 py-2 font-semibold">Keterangan</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(monthlyForm.rows).map(([empId, r]) => {
                  const emp = empMap[empId] || {};
                  return (
                    <tr key={empId} className="border-t border-border">
                      <td className="px-3 py-2 font-semibold"><div>{emp.name || empId}</div><div className="text-[10px] text-muted-foreground">{lembagaMap[emp.lembaga_id] || "-"}</div></td>
                      <td className="px-1 py-1"><input type="number" className="w-20 text-right border border-border rounded px-1 py-1 bg-background" value={r.durasi_menit} onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, durasi_menit: e.target.value } } })} data-testid={`biah-monthly-${empId}-durasi`} /></td>
                      <td className="px-1 py-1"><input type="number" className="w-20 text-right border border-border rounded px-1 py-1 bg-background" value={r.jumlah_halaman} onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, jumlah_halaman: e.target.value } } })} data-testid={`biah-monthly-${empId}-halaman`} /></td>
                      <td className="px-1 py-1"><input type="number" step="0.1" className="w-20 text-right border border-border rounded px-1 py-1 bg-background" value={r.rata_rata_halaman_per_hari ?? ""} placeholder="auto" onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, rata_rata_halaman_per_hari: e.target.value } } })} data-testid={`biah-monthly-${empId}-avg`} /></td>
                      <td className="px-1 py-1 text-center">
                        <input type="checkbox" checked={!!r.target_tercapai} onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, target_tercapai: e.target.checked } } })} data-testid={`biah-monthly-${empId}-target`} />
                      </td>
                      <td className="px-1 py-1"><input type="text" className="w-40 border border-border rounded px-1 py-1 bg-background" value={r.keterangan || ""} onChange={(e) => setMonthlyForm({ ...monthlyForm, rows: { ...monthlyForm.rows, [empId]: { ...r, keterangan: e.target.value } } })} placeholder="Juz / kendala" data-testid={`biah-monthly-${empId}-ket`} /></td>
                    </tr>
                  );
                })}
                {!Object.keys(monthlyForm.rows).length && <tr><td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">Tidak ada karyawan</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!isKaryawan && tab === "rekap" && (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-3 border-b border-border flex flex-wrap gap-3 items-end">
            <div><Label className="text-xs">Periode</Label><Input type="month" value={filterPeriod} onChange={(e) => setFilterPeriod(e.target.value)} className="w-40" data-testid="biah-rekap-period" /></div>
            <div><Label className="text-xs">Lembaga</Label>
              <Select value={filterLembaga} onValueChange={setFilterLembaga}>
                <SelectTrigger className="w-56" data-testid="biah-rekap-lembaga"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Seluruh Lembaga</SelectItem>{lembagas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={exportRekapExcel} data-testid="biah-rekap-excel"><Download size={14} className="mr-1.5" />Excel</Button>
              <Button size="sm" onClick={printRekap} data-testid="biah-rekap-pdf"><Printer size={14} className="mr-1.5" />Cetak PDF</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="px-3 py-2">Guru</th><th className="px-3 py-2">Jabatan</th><th className="px-3 py-2">Lembaga</th><th className="px-3 py-2 text-right">Durasi (mnt)</th><th className="px-3 py-2 text-right">Halaman</th><th className="px-3 py-2 text-right">Avg/Hari</th><th className="px-3 py-2 text-center">Target</th><th className="px-3 py-2">Keterangan</th></tr>
              </thead>
              <tbody>
                {rekap?.rows?.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-semibold">{r.name || r.employee_id}</td>
                    <td className="px-3 py-2 text-muted-foreground">{r.position || "-"}</td>
                    <td className="px-3 py-2">{r.lembaga_name || "-"}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.durasi_menit}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.jumlah_halaman}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.rata_rata_halaman_per_hari}</td>
                    <td className="px-3 py-2 text-center">{r.target_tercapai ? <span className="text-emerald-700 font-bold">YA</span> : <span className="text-rose-700 font-bold">TIDAK</span>}</td>
                    <td className="px-3 py-2 text-xs">{r.keterangan || "-"}</td>
                  </tr>
                ))}
                {!rekap?.rows?.length && <tr><td colSpan={8} className="px-5 py-10 text-center text-muted-foreground">Tidak ada data</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="biah-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Buat"} Rapor Bi&apos;ah Qur&apos;an</DialogTitle>
            <DialogDescription>Catat tilawah/tahfidz per bulan.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Guru/Karyawan *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="biah-employee-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{employees.map((e) => <SelectItem key={e.employee_id} value={e.employee_id}>{e.employee_id} · {e.name}</SelectItem>)}</SelectContent>
              </Select>
              {form.employee_id && empMap[form.employee_id] && (
                <div className="mt-1.5 text-xs text-muted-foreground">Jabatan: <span className="font-semibold text-foreground">{empMap[form.employee_id].position || "-"}</span></div>
              )}
            </div>
            <div><Label>Rapor Bi&apos;ah Bulan *</Label><Input type="month" value={form.period || ""} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="biah-period-input" /></div>
            <div></div>
            <div><Label>Jumlah Durasi (menit)</Label><Input type="number" min={0} value={form.durasi_menit || 0} onChange={(e) => setForm({ ...form, durasi_menit: e.target.value })} data-testid="biah-durasi" /></div>
            <div><Label>Jumlah Halaman</Label><Input type="number" min={0} value={form.jumlah_halaman || 0} onChange={(e) => setForm({ ...form, jumlah_halaman: e.target.value })} data-testid="biah-halaman" /></div>
            <div><Label>Rata-Rata Halaman/Hari <span className="text-xs text-muted-foreground">(kosongkan = auto)</span></Label><Input type="number" step="0.1" value={form.rata_rata_halaman_per_hari ?? ""} onChange={(e) => setForm({ ...form, rata_rata_halaman_per_hari: e.target.value })} data-testid="biah-avg" /></div>
            <div>
              <Label>Target Tercapai</Label>
              <Select value={form.target_tercapai ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, target_tercapai: v === "yes" })}>
                <SelectTrigger data-testid="biah-target"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">YA (Hijau)</SelectItem><SelectItem value="no">TIDAK (Merah)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Keterangan (Juz terakhir / kendala)</Label><Textarea rows={2} value={form.keterangan || ""} onChange={(e) => setForm({ ...form, keterangan: e.target.value })} data-testid="biah-keterangan" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="biah-save-button">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
